import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Tag, ArrowUpRight } from "@phosphor-icons/react";
import { MaskedLine, Reveal, Overline } from "../components/Kinetic";
import { TWO_A_URL, EvalNote } from "../components/EstimateCTA";
import { Seo, SITE } from "../components/Seo";
import { EditorialMarquee } from "../components/Marquee";
import { imgSrc } from "../lib/api";

export default function CategoryPage({ category }) {
  const Icon = category.icon;
  return (
    <main data-testid={`category-page-${category.slug}`}>
      <Seo title={category.metaTitle} description={category.metaDescription} path={`/${category.slug}`} image={imgSrc(category.image)}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE },
            { "@type": "ListItem", position: 2, name: category.title, item: `${SITE}/${category.slug}` },
          ],
        }]} />

      <section className="pt-32 md:pt-44">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Overline className="flex items-center gap-2">
              <Icon size={18} weight="duotone" className="text-brand" /> Sell Your {category.short} · Chicago
            </Overline>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.0] tracking-tighter sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.15}>{category.title}</MaskedLine>
            </h1>
            <Reveal delay={0.4}>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-[#4A4A4A]">{category.intro}</p>
            </Reveal>
            <Reveal delay={0.5}>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid={`${category.slug}-estimate-btn`}
                  className="group flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-navy">
                  Get a Buyback Estimate
                  <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
                </a>
                <Link to="/how-it-works" data-testid={`${category.slug}-how-it-works-btn`}
                  className="rounded-full border border-black/15 px-8 py-4 font-display text-sm font-bold uppercase tracking-[0.12em] text-navy transition-colors duration-300 hover:bg-navy hover:text-white">
                  How It Works
                </Link>
                <EvalNote className="basis-full pt-1" />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.3} className="lg:col-span-5">
            <div className="spotlight-frame overflow-hidden rounded-[2rem]">
              <img src={imgSrc(category.image)} alt={category.imageAlt} className="h-[340px] w-full object-cover md:h-[440px]" loading="eager" />
            </div>
          </Reveal>
        </div>
      </section>

      <div className="mt-20"><EditorialMarquee /></div>

      <section className="py-24 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 md:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal><Overline>Accepted Models</Overline></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tighter md:text-4xl">What we buy</h2>
            </Reveal>
            <div className="mt-8 flex flex-wrap gap-3">
              {category.models.map((m) => (
                <Reveal key={m} delay={0.05}>
                  <span className="inline-block rounded-full border border-black/10 bg-[#F5F5F7] px-5 py-2.5 text-sm font-medium text-navy">{m}</span>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <h2 className="mt-16 font-display text-3xl font-extrabold tracking-tighter md:text-4xl">Condition requirements</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-[#4A4A4A]">
                We purchase eligible working electronics in good condition. We do not buy broken devices.
                Your {category.short.toLowerCase()} should meet these basics:
              </p>
            </Reveal>
            <ul className="mt-7 flex flex-col gap-3.5">
              {category.conditionPoints.map((p) => (
                <Reveal key={p} delay={0.05}>
                  <li className="flex items-start gap-3 text-base text-black/85">
                    <CheckCircle size={22} weight="duotone" className="mt-0.5 shrink-0 text-cash" /> {p}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <div className="rounded-3xl border border-black/10 bg-[#F5F5F7] p-8" data-testid={`${category.slug}-value-factors`}>
                <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-[#8A8A8A]">
                  <Tag size={16} weight="duotone" className="text-brand" /> What affects your offer
                </p>
                <ul className="mt-6 flex flex-col gap-4">
                  {category.valueFactors.map((f) => (
                    <li key={f} className="border-b border-black/5 pb-4 font-display text-lg font-semibold tracking-tight text-navy last:border-0 last:pb-0">{f}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-6 rounded-3xl border border-navy/15 bg-navy p-8 text-white">
                <p className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">Before you visit</p>
                <p className="mt-3 text-sm leading-relaxed text-white/80">
                  Prepare your device first — it protects your data and keeps it eligible:
                </p>
                <ul className="mt-5 flex flex-col gap-3">
                  {category.prepLinks.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="group flex items-center justify-between gap-3 text-sm font-semibold text-white transition-colors hover:text-white/70">
                        {l.label}
                        <ArrowUpRight size={15} weight="bold" className="shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-[#F5F5F7] py-20 text-center">
        <Reveal>
          <h2 className="mx-auto max-w-2xl px-6 font-display text-3xl font-extrabold tracking-tighter md:text-5xl">
            Find out what your {category.short.toLowerCase()} is worth.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <a href={TWO_A_URL} target="_blank" rel="noopener" data-testid={`${category.slug}-bottom-estimate-btn`}
            className="group mt-9 inline-flex items-center gap-2 rounded-full bg-brand px-10 py-5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-navy">
            Get a Buyback Estimate
            <ArrowRight size={16} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>
    </main>
  );
}
